<script lang="ts" setup>
import { GpAlert, GpTextInput } from '@gportal/uikit';
import AsyncModal from 'gpcore-uikit/components/new/AsyncModal.vue';
import { computed } from 'vue';
import { CloudProviderType } from 'gpcore-grpc/gpcore/type/v1/cloud_provider_pb';
import useFormData from 'gpcore-uikit/composables/useFormData';
import onModalShow from 'gpcore-uikit/composables/onModalShow';
import type { UpdateProjectCloudProviderResponse } from 'gpcore-grpc/gpcore/api/cloud/v1/responses_pb';
import type { ProjectCloudProvider } from 'gpcore-grpc/gpcore/api/cloud/v1/project_pb';
import { useProjectStore } from 'gpcore-apikit/store/pinia/project';

const props = withDefaults(
  defineProps<{
    show?: boolean;
    cloudProvider?: ProjectCloudProvider;
  }>(),
  {
    show: false,
    cloudProvider: undefined,
  },
);

const emit = defineEmits<{
  (e: 'confirm', cloudProvider: ProjectCloudProvider): void;
  (e: 'close'): void;
}>();

const initialValues = {
  name: '',
  type: { key: CloudProviderType.UNSPECIFIED, label: null },
  region: '',
  apiKeyId: '',
  apiKeySecret: '',
};

const { resetForm, form } = useFormData(initialValues);

const isValid = computed(() => {
  return (
    form.name.length >= 5 && form.type.key !== CloudProviderType.UNSPECIFIED && form.region !== ''
  );
});

const projectStore = useProjectStore();
const edit = () => {
  if (!isValid.value) {
    return;
  }

  return projectStore.updateProjectCloudProvider({
    id: props.cloudProvider.id,
    name: form.name,
    region: form.region,
    apiKeyId: form.apiKeyId,
    apiKeySecret: form.apiKeySecret,
  });
};

const onDone = (response: UpdateProjectCloudProviderResponse) => {
  emit('confirm', response.cloudProvider);
};

onModalShow(props, () => {
  if (props.cloudProvider) {
    initialValues.name = props.cloudProvider.name;
    initialValues.type = { key: props.cloudProvider.type, label: null };
    initialValues.region = props.cloudProvider.region;
  }

  resetForm();
});
</script>

<template>
  <AsyncModal
    :show="props.show"
    :header-icon="['fas', 'fa-cloud']"
    header-text="Edit Cloud Provider"
    :async-function="edit"
    confirm-text="Save"
    :is-valid="isValid"
    @close="emit('close')"
    @done="onDone"
  >
    <template #content>
      <div class="tw-mb-8 tw-px-4">
        <GpTextInput
          placeholder="Free identifier for your cloud provider"
          input-id="add-name"
          name="name"
          :required="true"
          label="Name"
          v-model="form.name"
        />
        <GpTextInput
          placeholder="External cloud provider region (e.g. us-west-1)"
          input-id="add-region"
          name="region"
          :required="true"
          label="Region"
          v-model="form.region"
        />
        <p class="tw-mb-4 tw-mt-2">
          Make sure you are using the correct region identifier specified by your cloud provider.
          This will be checked after creating/editing the cloud provider.
        </p>
        <GpTextInput
          placeholder="Your API key ID"
          input-id="add-api-key"
          name="api-key"
          :required="true"
          label="API key ID"
          v-model="form.apiKeyId"
        />
        <GpTextInput
          placeholder="Your API key secret"
          input-id="add-api-secret"
          name="secret"
          :required="true"
          label="API key secret"
          v-model="form.apiKeySecret"
        />
        <p class="tw-mb-4 tw-mt-2">
          API key ID and secret are used to authenticate against your cloud provider. Both values
          will be stored encrypted and will never be shown on the frontend or exposed through our
          API.
        </p>

        <GpAlert
          class="tw-mt-4"
          alert-type="info"
          message="Leave the API key ID and API key secret empty, if you do not want to change them."
        />
      </div>
    </template>
  </AsyncModal>
</template>
